// Spell definitions ported from Java Spell.java
// 40+ spells across 11 types

export const SPELL_TYPE = {
  ATK: 0,            // Attack damage
  HEAL: 1,           // Heal HP
  PROTECT: 2,        // Increase armor
  MYSTIFICATION: 3,  // Skip turns
  STR: 4,            // Increase strength
  STOP: 5,           // Prevent movement
  REMOVE_SPELL: 6,   // Remove active spells
  DRAIN_HEALTH: 7,   // Steal HP
  DRAIN_MANA: 8,     // Steal mana
  BLESSING: 9,       // Increase hit chance
  CURSE: 10,         // Decrease hit chance
} as const;

export type SpellType = typeof SPELL_TYPE[keyof typeof SPELL_TYPE];

// Spell type names in Finnish
export const SPELL_TYPE_NAMES: Record<SpellType, string> = {
  [SPELL_TYPE.ATK]: 'Hyökkäys',
  [SPELL_TYPE.HEAL]: 'Parannus',
  [SPELL_TYPE.PROTECT]: 'Suojaus',
  [SPELL_TYPE.MYSTIFICATION]: 'Hämäys',
  [SPELL_TYPE.STR]: 'Voimakkuus',
  [SPELL_TYPE.STOP]: 'Pysäytys',
  [SPELL_TYPE.REMOVE_SPELL]: 'Poista taikuus',
  [SPELL_TYPE.DRAIN_HEALTH]: 'Ime kesto',
  [SPELL_TYPE.DRAIN_MANA]: 'Ime mana',
  [SPELL_TYPE.BLESSING]: 'Siunaus',
  [SPELL_TYPE.CURSE]: 'Kirous',
};

export interface Spell {
  id: string;
  name: string;
  type: SpellType;
  price: number;
  manaCost: number;
  strength: string;   // dice roll or fixed value
  duration?: number;  // turns the effect lasts (-1 = instant)
}

// Friendly spell types (target allies)
const FRIENDLY_SPELL_TYPES: SpellType[] = [
  SPELL_TYPE.HEAL,
  SPELL_TYPE.PROTECT,
  SPELL_TYPE.STR,
  SPELL_TYPE.REMOVE_SPELL,
  SPELL_TYPE.BLESSING,
];

// Helper to check if spell targets friendly
export function isFriendlySpell(spell: Spell): boolean {
  return FRIENDLY_SPELL_TYPES.includes(spell.type);
}

// Attack spells
export const ATTACK_SPELLS: Spell[] = [
  { id: 'haava', name: 'Haava', type: SPELL_TYPE.ATK, price: 100, manaCost: 1, strength: '1d3' },
  { id: 'vesivasama', name: 'Vesivasama', type: SPELL_TYPE.ATK, price: 200, manaCost: 2, strength: '2d3' },
  { id: 'valovasama', name: 'Valovasama', type: SPELL_TYPE.ATK, price: 300, manaCost: 3, strength: '3d3' },
  { id: 'jaatava-polte', name: 'Jäätävä Polte', type: SPELL_TYPE.ATK, price: 400, manaCost: 4, strength: '4d3' },
  { id: 'turjake', name: 'Turjake', type: SPELL_TYPE.ATK, price: 500, manaCost: 5, strength: '5d3' },
  { id: 'synkka-tuomio', name: 'Synkkä Tuomio', type: SPELL_TYPE.ATK, price: 600, manaCost: 6, strength: '6d3' },
];

// Heal spells
export const HEAL_SPELLS: Spell[] = [
  { id: 'auttava-kasi', name: 'Auttava Käsi', type: SPELL_TYPE.HEAL, price: 100, manaCost: 1, strength: '5' },
  { id: 'henkinen-parannus', name: 'Henkinen parannus', type: SPELL_TYPE.HEAL, price: 200, manaCost: 2, strength: '10' },
  { id: 'mielenrauha', name: 'Mielenrauha', type: SPELL_TYPE.HEAL, price: 300, manaCost: 3, strength: '15' },
  { id: 'parannusvakuutus', name: 'Parannusvakuutus', type: SPELL_TYPE.HEAL, price: 400, manaCost: 4, strength: '20' },
  { id: 'tosi-terveys', name: 'Tosi Terveys', type: SPELL_TYPE.HEAL, price: 500, manaCost: 5, strength: '25' },
  { id: 'pappisparannus', name: 'Pappisparannus', type: SPELL_TYPE.HEAL, price: 600, manaCost: 6, strength: '30' },
];

// Protection spells
export const PROTECTION_SPELLS: Spell[] = [
  { id: 'sateensuoja', name: 'Sateensuoja', type: SPELL_TYPE.PROTECT, price: 100, manaCost: 1, strength: '1', duration: 10 },
  { id: 'suojataika', name: 'Suojataika', type: SPELL_TYPE.PROTECT, price: 200, manaCost: 2, strength: '2', duration: 10 },
  { id: 'taydellinen-suoja', name: 'Täydellinen Suoja', type: SPELL_TYPE.PROTECT, price: 300, manaCost: 3, strength: '3', duration: 10 },
  { id: 'tosi-panssari', name: 'Tosi Panssari', type: SPELL_TYPE.PROTECT, price: 400, manaCost: 4, strength: '4', duration: 10 },
];

// Mystification (skip turns)
export const MYSTIFICATION_SPELLS: Spell[] = [
  { id: 'lentava-sika', name: 'Lentävä Sika', type: SPELL_TYPE.MYSTIFICATION, price: 200, manaCost: 2, strength: '', duration: 1 },
  { id: 'nikotuskohtaus', name: 'Nikotuskohtaus', type: SPELL_TYPE.MYSTIFICATION, price: 400, manaCost: 4, strength: '', duration: 2 },
];

// Strength buffs
export const STRENGTH_SPELLS: Spell[] = [
  { id: 'vitamiinipilleri', name: 'Vitamiinipilleri', type: SPELL_TYPE.STR, price: 100, manaCost: 1, strength: '5', duration: 10 },
  { id: 'voiman-tunto', name: 'Voiman Tunto', type: SPELL_TYPE.STR, price: 200, manaCost: 2, strength: '10', duration: 10 },
  { id: 'punttikuuri', name: 'Punttikuuri', type: SPELL_TYPE.STR, price: 300, manaCost: 3, strength: '15', duration: 10 },
  { id: 'tosi-lihas', name: 'Tosi Lihas', type: SPELL_TYPE.STR, price: 400, manaCost: 4, strength: '20', duration: 10 },
];

// Stop (root) spells
export const STOP_SPELLS: Spell[] = [
  { id: 'jalkakramppi', name: 'Jalkakramppi', type: SPELL_TYPE.STOP, price: 100, manaCost: 1, strength: '', duration: 2 },
  { id: 'tervalatakko', name: 'Tervalätäkkö', type: SPELL_TYPE.STOP, price: 200, manaCost: 2, strength: '', duration: 4 },
  { id: 'jalkakahle', name: 'Jalkakahle', type: SPELL_TYPE.STOP, price: 300, manaCost: 3, strength: '', duration: 6 },
];

// Remove spell (dispel)
export const REMOVE_SPELL_SPELLS: Spell[] = [
  { id: 'loitsutuho', name: 'Loitsutuho', type: SPELL_TYPE.REMOVE_SPELL, price: 200, manaCost: 2, strength: '' },
];

// Drain health (vampirism)
export const DRAIN_HEALTH_SPELLS: Spell[] = [
  { id: 'kevyt-vampyrismi', name: 'Kevyt Vampyrismi', type: SPELL_TYPE.DRAIN_HEALTH, price: 100, manaCost: 1, strength: '1d2' },
  { id: 'lieva-vampyrismi', name: 'Lievä Vampyrismi', type: SPELL_TYPE.DRAIN_HEALTH, price: 200, manaCost: 2, strength: '2d2' },
  { id: 'ikava-vampyrismi', name: 'Ikävä Vampyrismi', type: SPELL_TYPE.DRAIN_HEALTH, price: 300, manaCost: 3, strength: '3d2' },
  { id: 'tyly-vampyrismi', name: 'Tyly Vampyrismi', type: SPELL_TYPE.DRAIN_HEALTH, price: 400, manaCost: 4, strength: '4d2' },
  { id: 'karu-vampyrismi', name: 'Karu Vampyrismi', type: SPELL_TYPE.DRAIN_HEALTH, price: 500, manaCost: 5, strength: '5d2' },
  { id: 'tosi-vampyrismi', name: 'Tosi Vampyrismi', type: SPELL_TYPE.DRAIN_HEALTH, price: 600, manaCost: 6, strength: '6d2' },
];

// Drain mana
export const DRAIN_MANA_SPELLS: Spell[] = [
  { id: 'kevyt-manaimu', name: 'Kevyt Manaimu', type: SPELL_TYPE.DRAIN_MANA, price: 100, manaCost: 1, strength: '5' },
  { id: 'lieva-manaimu', name: 'Lievä Manaimu', type: SPELL_TYPE.DRAIN_MANA, price: 200, manaCost: 2, strength: '10' },
  { id: 'ikava-manaimu', name: 'Ikävä Manaimu', type: SPELL_TYPE.DRAIN_MANA, price: 300, manaCost: 3, strength: '15' },
  { id: 'tyly-manaimu', name: 'Tyly Manaimu', type: SPELL_TYPE.DRAIN_MANA, price: 400, manaCost: 4, strength: '20' },
  { id: 'karu-manaimu', name: 'Karu Manaimu', type: SPELL_TYPE.DRAIN_MANA, price: 500, manaCost: 5, strength: '25' },
  { id: 'tosi-manaimu', name: 'Tosi Manaimu', type: SPELL_TYPE.DRAIN_MANA, price: 600, manaCost: 6, strength: '30' },
];

// Blessings (hit chance buff)
export const BLESSING_SPELLS: Spell[] = [
  { id: 'onnentoivotus', name: 'Onnentoivotus', type: SPELL_TYPE.BLESSING, price: 100, manaCost: 1, strength: '5', duration: 10 },
  { id: 'janiksenkapala', name: 'Jäniksenkaapälä', type: SPELL_TYPE.BLESSING, price: 200, manaCost: 2, strength: '10', duration: 10 },
  { id: 'neliapila', name: 'Neliapila', type: SPELL_TYPE.BLESSING, price: 300, manaCost: 3, strength: '15', duration: 10 },
  { id: 'tosi-siunaus', name: 'Tosi Siunaus', type: SPELL_TYPE.BLESSING, price: 400, manaCost: 4, strength: '20', duration: 10 },
];

// Curses (hit chance debuff)
export const CURSE_SPELLS: Spell[] = [
  { id: 'epaonnentoivotus', name: 'Epäonnentoivotus', type: SPELL_TYPE.CURSE, price: 100, manaCost: 1, strength: '5', duration: 10 },
  { id: 'paha-silma', name: 'Paha Silmä', type: SPELL_TYPE.CURSE, price: 200, manaCost: 2, strength: '10', duration: 10 },
  { id: 'paha-kurki', name: 'Paha Kurki', type: SPELL_TYPE.CURSE, price: 300, manaCost: 3, strength: '15', duration: 10 },
  { id: 'tosi-kirous', name: 'Tosi Kirous', type: SPELL_TYPE.CURSE, price: 400, manaCost: 4, strength: '20', duration: 10 },
];

// All spells combined
export const ALL_SPELLS: Spell[] = [
  ...ATTACK_SPELLS,
  ...HEAL_SPELLS,
  ...PROTECTION_SPELLS,
  ...MYSTIFICATION_SPELLS,
  ...STRENGTH_SPELLS,
  ...STOP_SPELLS,
  ...REMOVE_SPELL_SPELLS,
  ...DRAIN_HEALTH_SPELLS,
  ...DRAIN_MANA_SPELLS,
  ...BLESSING_SPELLS,
  ...CURSE_SPELLS,
];

// Get spells by type
export function getSpellsByType(type: SpellType): Spell[] {
  return ALL_SPELLS.filter(spell => spell.type === type);
}

// Find spell by name
export function findSpellByName(name: string): Spell | undefined {
  return ALL_SPELLS.find(spell => spell.name === name);
}

// Find spell by ID
export function findSpellById(id: string): Spell | undefined {
  return ALL_SPELLS.find(spell => spell.id === id);
}
